import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { pricingPlanMessages } from 'src/features/pricing/messages';
import { useDeleteSitePricingPlan } from 'src/features/pricing/sites/api/useDeleteSitePricingPlan';
import { LIST_SITE_PRICING_PLANS_RESOURCE } from 'src/features/pricing/sites/api/useListSitePricingPlans';
import { SitePricingPlan } from 'src/features/pricing/sites/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface PricingPlanFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedSitePricingPlan: SitePricingPlan;
    children?: React.ReactNode;
}

export default function DeleteSitePricingPlanFormModal<TData>({
    selectedSitePricingPlan,
    semantics,
    children,
}: PricingPlanFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { mutate: deletePricingPlan, isPending: isPendingDelete } = useDeleteSitePricingPlan({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SITE_PRICING_PLANS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(pricingPlanMessages.deletePricingPlanSuccess),
                description: formatMessage(pricingPlanMessages.deletePricingPlanSuccessDescription, {
                    entry: selectedSitePricingPlan.translations.find((translation) => translation.language === locale)
                        ?.name,
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = () => deletePricingPlan({ planId: selectedSitePricingPlan.id, parkingId });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.DELETE} />
                        <div>
                            <DialogTitle>
                                <FormattedMessage
                                    id={generalMessages.deleteEntry.id}
                                    defaultMessage={generalMessages.deleteEntry.defaultMessage}
                                    values={{ entry: semantics?.entryName?.toLowerCase() }}
                                />
                            </DialogTitle>
                            <DialogDescription>
                                <FormattedMessage
                                    id={pricingPlanMessages.deletePricingPlanSubtitle.id}
                                    defaultMessage={pricingPlanMessages.deletePricingPlanSubtitle.defaultMessage}
                                    values={{
                                        entry: '',
                                    }}
                                />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        <FormattedMessage
                            id={pricingPlanMessages.confirmDeletePricingPlan.id}
                            defaultMessage={pricingPlanMessages.confirmDeletePricingPlan.defaultMessage}
                            values={{
                                entry: '',
                            }}
                        />
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructiveLight"
                        disabled={isPendingDelete}
                        type="submit"
                        onClick={onSubmit}
                        className="flex-1"
                    >
                        {isPendingDelete && (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                        )}
                        <FormattedMessage
                            id={generalMessages.deleteEntry.id}
                            defaultMessage={generalMessages.deleteEntry.defaultMessage}
                            values={{ entry: semantics?.entryName?.toLowerCase() }}
                        />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
